import React, { useState, useCallback, useEffect, memo, useRef } from 'react';
import { ExternalLink, X } from 'lucide-react';
import PropTypes from 'prop-types';
import useFocusTrap from '../../hooks/useFocusTrap';
import './UnidentifiedSourcesModal.css';

// =========================================
// Constants
// =========================================
const SUCCESS_ANIMATION_DURATION = 3000;
const MAX_SOURCE_NAME_LENGTH = 60;
const VALID_NAME_PATTERN = /^[\p{L}\p{N}\s\-_.,'()&]+$/u;

// =========================================
// Component Definition
// =========================================
const UnidentifiedSourcesModal = ({
  isOpen,
  onClose,
  unidentifiedSources,
  onSourceCorrection
}) => {
  // =========================================
  // State Management
  // =========================================
  const [sourceNames, setSourceNames] = useState({});
  const [processingDomains, setProcessingDomains] = useState(new Set());
  const [successDomains, setSuccessDomains] = useState(new Set());
  const [visibleSources, setVisibleSources] = useState([]);
  const modalRef = useRef(null);

  // =========================================
  // Focus Trap
  // =========================================
  useFocusTrap(modalRef, isOpen);

  // =========================================
  // Initialization and Cleanup Effect
  // =========================================
  useEffect(() => {
    if (isOpen) {
      const initialNames = {};
      unidentifiedSources.forEach(source => {
        if (source.suggestedName) {
          initialNames[source.domain] = source.suggestedName;
        }
      });
      setSourceNames(initialNames);
      setVisibleSources(unidentifiedSources);
    } else {
      setSourceNames({});
      setProcessingDomains(new Set());
      setSuccessDomains(new Set());
      setVisibleSources([]);
    }
  }, [isOpen, unidentifiedSources]);

  // =========================================
  // Escape Key Handler
  // =========================================
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // =========================================
  // Event Handlers
  // =========================================
  const handleInputChange = useCallback((domain, value) => {
    if (value.length <= MAX_SOURCE_NAME_LENGTH) {
      setSourceNames(prev => ({ ...prev, [domain]: value }));
    }
  }, []);

  const validateSourceName = useCallback((name) => {
    if (!name?.trim()) return false;
    return VALID_NAME_PATTERN.test(name);
  }, []);

  const handleSave = useCallback(async (domain) => {
    const sourceName = sourceNames[domain];

    if (!validateSourceName(sourceName)) {
      return;
    }

    setProcessingDomains(prev => new Set([...prev, domain]));

    try {
      await onSourceCorrection(domain, sourceName);

      setProcessingDomains(prev => {
        const updated = new Set(prev);
        updated.delete(domain);
        return updated;
      });

      setSuccessDomains(prev => new Set([...prev, domain]));

      setTimeout(() => {
        setSuccessDomains(prev => {
          const updated = new Set(prev);
          updated.delete(domain);
          return updated;
        });

        setVisibleSources(prev => prev.filter(s => s.domain !== domain));

        setSourceNames(prev => {
          const updated = { ...prev };
          delete updated[domain];
          return updated;
        });
      }, SUCCESS_ANIMATION_DURATION);

    } catch (error) {
      console.error('Source correction failed:', { domain, error: error.message });
      setProcessingDomains(prev => {
        const updated = new Set(prev);
        updated.delete(domain);
        return updated;
      });
    }
  }, [sourceNames, onSourceCorrection, validateSourceName]);

  // Early return after all hooks
  if (!isOpen) return null;

  // =========================================
  // Render
  // =========================================
  return (
    <>
      <div
        className="usm-backdrop"
        onClick={onClose}
        role="presentation"
      />
      <div
        className="usm-modal"
        ref={modalRef}
        role="dialog"
        aria-labelledby="usm-title"
        aria-modal="true"
      >
        <div className="usm-header">
          <h2 id="usm-title">Your Unidentified Sources</h2>
          <button
            className="usm-close-button"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <p className="usm-instructions">
          Help make Constellation KG better for everyone by fixing these
          unidentified sources to make it easier for you to find the information
          that you&#39;re looking for in the future
        </p>

        <div className="usm-table-container">
          <div className="usm-list-header">
            <span>URL</span>
            <span>Source Name</span>
          </div>
          <div className="usm-list">
            {visibleSources.map(({ domain }) => {
              const displayUrl = domain.includes('http')
                ? new URL(domain).hostname
                : domain;

              return (
                <div
                  key={domain}
                  className={successDomains.has(domain) ? 'usm-row success' : 'usm-row'}
                >
                  <div className="usm-url-cell">
                    <span className="usm-domain-text" title={domain}>
                      {displayUrl}
                    </span>
                    <button
                      className="usm-link-button"
                      onClick={() => window.open(`https://${domain}`, '_blank', 'noopener,noreferrer')}
                      aria-label={`Open ${domain} in new tab`}
                    >
                      <ExternalLink size={16} />
                    </button>
                  </div>
                  <div className="usm-input-cell">
                    <input
                      type="text"
                      value={sourceNames[domain] || ''}
                      onChange={(e) => handleInputChange(domain, e.target.value)}
                      placeholder="Type source name here"
                      disabled={processingDomains.has(domain)}
                      maxLength={MAX_SOURCE_NAME_LENGTH}
                      aria-label={`Source name for ${domain}`}
                    />
                    <button
                      className="usm-save-button"
                      onClick={() => handleSave(domain)}
                      disabled={!validateSourceName(sourceNames[domain]) || processingDomains.has(domain)}
                      aria-label={`Save source name for ${domain}`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

// =========================================
// Prop Types
// =========================================
UnidentifiedSourcesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  unidentifiedSources: PropTypes.arrayOf(
    PropTypes.shape({
      domain: PropTypes.string.isRequired,
      suggestedName: PropTypes.string
    })
  ).isRequired,
  onSourceCorrection: PropTypes.func.isRequired
};

export default memo(UnidentifiedSourcesModal);
